// admin/users/UsersList.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  ArrowUpDown, 
  ChevronDown
} from "lucide-react";
import { toast } from "react-toastify";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import UserDetailDrawer from "./UserDetailDrawer";
import FilterPanel from "../../common/FilterPanel";
import Button from "../../common/Button";
import Badge from "../../common/Badge";
import { fetchAdminUsers, fetchAdminUserById, clearAuditNav } from "@/store/slices/adminSlice";

const DISTANCE_OPTIONS = [
  { value: "koset_distance", label: "Koset Distance" },
  { value: "activity_distance", label: "Activity Distance" },
  { value: "total", label: "Total Distance" },
];

const SEARCH_FIELDS = [
  { value: "search_username", label: "Username" },
  { value: "search_name", label: "Name" },
  { value: "search_email", label: "Email" },
  { value: "search", label: "Global" },
];

const SORT_OPTIONS = [
  { value: "recent", label: "Recent" },
  { value: "name", label: "Name" },
  { value: "username", label: "Username" },
  { value: "total_rides", label: "Rides" },
  { value: "koset_distance", label: "Koset Distance" },
  { value: "activity_distance", label: "Activity Distance" },
];

const ROLE_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

// Lightweight inline dropdown with styled list
const InlineDropdown = ({
  options,
  value,
  onChange,
  className = "",
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange({ target: { value: opt.value } });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`flex items-center justify-between gap-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
          compact ? "px-2 py-1 text-sm" : "px-3 py-2 text-sm w-full"
        }`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute z-60 mt-1 w-full min-w-max bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt, i) => (
            <div
              key={opt.value}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt);
              }}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                value === opt.value
                  ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium"
                  : i === highlight
                    ? "bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UsersList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    users,
    usersPage,
    usersTotalPages,
    usersLimit,
    isLoadingUsers,
    usersError,
    auditNavEntity,
  } = useSelector((state) => state.admin);

  const [selectedId, setSelectedId] = useState(null);
  const selectedUser = selectedId ? users.find((u) => u._id === selectedId) : null;
  const [distanceType, setDistanceType] = useState("total");

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    searchField: "search_username",
    role: "all",
  });

  // Sorting states
  const [sorting, setSorting] = useState({
    field: "recent",
    order: "desc",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ─── Audit Log Deep-Link: auto-open drawer when ?id= is in URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkId = params.get('id');
    if (deepLinkId) {
      navigate(location.pathname, { replace: true });
      dispatch(fetchAdminUserById(deepLinkId));
    }
    return () => { dispatch(clearAuditNav()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auditNavEntity && auditNavEntity._id) {
      setSelectedId(auditNavEntity._id);
    }
  }, [auditNavEntity]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch users when filters, sorting, or page changes
  const fetchUsersList = useCallback(
    (page = 1) => {
      const apiFilters = {
        page,
        limit: usersLimit,
        role: filters.role !== "all" ? filters.role : undefined,
        sort: sorting.field,
        order: sorting.order,
      };

      // Add search parameter based on selected field
      if (debouncedSearch.trim()) {
        apiFilters[filters.searchField] = debouncedSearch.trim();
      }

      dispatch(fetchAdminUsers(apiFilters));
    },
    [
      dispatch,
      usersLimit,
      debouncedSearch,
      filters.searchField,
      filters.role,
      sorting,
    ]
  );

  useEffect(() => {
    fetchUsersList(1);
  }, [fetchUsersList]);

  const handlePageChange = (newPage) => {
    fetchUsersList(newPage);
  };

  const handleSortToggle = (field) => {
    setSorting((prev) => ({
      field,
      order: prev.field === field && prev.order === "desc" ? "asc" : "desc",
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      searchField: "search_username",
      role: "all",
    });
  };

  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.role !== "all" ||
    filters.searchField !== "search_username";

  // Get the appropriate distance value for display
  const getDistanceValue = (user) => {
    switch (distanceType) {
      case "koset_distance":
        return user.koset_distance?.toFixed(2) || "0.00";
      case "activity_distance":
        return user.activity_distance?.toFixed(2) || "0.00";
      case "total":
      default:
        const total =
          (user.koset_distance || 0) + (user.activity_distance || 0);
        return total.toFixed(2);
    }
  };


  // Custom header for distance column with dropdown
  const DistanceHeader = () => (
    <div className="flex items-center gap-2">
      <InlineDropdown
        options={DISTANCE_OPTIONS}
        value={distanceType}
        onChange={(e) => setDistanceType(e.target.value)}
        compact
      />
      <span className="text-xs text-gray-500">(km)</span>
    </div>
  );

  const getStatusBadgeTone = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "suspended":
        return "warning";
      case "banned":
      case "deleted":
        return "critical";
      default:
        return "neutral";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Users Management
        </h2>
        <Button variant="primary" size="sm">
          Export CSV
        </Button>
      </div>

      {/* Filters Section */}
      <FilterPanel
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Section */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Users
            </label>
            <div className="flex gap-2">
              <InlineDropdown
                options={SEARCH_FIELDS}
                value={filters.searchField}
                onChange={(e) =>
                  setFilters({ ...filters, searchField: e.target.value })
                }
                className="w-1/3"
              />
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                placeholder="Enter search term..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Role
            </label>
            <InlineDropdown
              options={ROLE_OPTIONS}
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            />
          </div>
        </div>
      </FilterPanel>

      {/* Sorting Section */}
      <FilterPanel
        title="Sorting"
        icon={ArrowUpDown}
        hasActiveFilters={sorting.field !== "recent"}
        onClearFilters={() => setSorting({ field: "recent", order: "desc" })}
        defaultOpen={false}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {SORT_OPTIONS.map((opt) => (
            <div key={opt.value}>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {opt.label}
              </label>
              <button
                onClick={() => handleSortToggle(opt.value)}
                className={`w-full px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                  sorting.field === opt.value
                    ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                    : "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
                } hover:shadow-sm`}
              >
                {sorting.field === opt.value
                  ? `${sorting.order === "desc" ? "↓" : "↑"}`
                  : "Not Sorted"}
              </button>
            </div>
          ))}
        </div>
      </FilterPanel>

      {/* Results Count & Error */}
      <div className="flex justify-between items-center px-1">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isLoadingUsers ? (
            "Loading users..."
          ) : (
            <>Showing {users.length} users</>
          )}
        </div>
        {usersError && (
          <div className="text-sm text-red-600 dark:text-red-400">
            {usersError}
          </div>
        )}
      </div>

      {/* Users Table */}
      {isLoadingUsers && users.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <Table
          columns={[
            { key: "name", title: "Name" },
            { key: "username", title: "Username" },
            { key: "email", title: "Email" },
            { key: "status", title: "Status" },
            { key: "rides", title: "Rides" },
            {
              key: "distance",
              title: <DistanceHeader />,
            },
            { key: "actions", title: "Actions" },
          ]}
          data={users}
          renderRow={(u) => (
            <tr
              key={u._id}
              className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
            >
              <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                {u.name}
              </td>
              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                @{u.username}
              </td>
              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                {u.email}
              </td>
              <td className="px-4 py-3">
                <Badge tone={getStatusBadgeTone(u.status)}>
                  {u.status || "active"}
                </Badge>
              </td>
              <td className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">
                {u.total_rides || 0}
              </td>
              <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-200">
                {getDistanceValue(u)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setSelectedId(u._id)}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs"
                  >
                    View
                  </Button>
                </div>
              </td>
            </tr>
          )}
        />
      )}

      {/* Empty State */}
      {!isLoadingUsers && users.length === 0 && !usersError && (
        <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">
            No users found matching your criteria.
          </p>
        </div>
      )}

      <Pagination
        page={usersPage}
        totalPages={usersTotalPages}
        onChange={handlePageChange}
      />

      {selectedUser && (
        <UserDetailDrawer 
          user={selectedUser} 
          onClose={() => setSelectedId(null)} 
        />
      )}

    </div>
  );
};

export default UsersList;
